import isEmpty from "lodash/isEmpty";
import { CounterpartyProfileWrite, Payer } from "@wingspanhq/wingspan-api-sdk";
import { linkRequestsApi, payersApi } from "@api";
import { queriesPayers } from "@query";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { QUERY_PAYER_ROWS } from "../../../query/search/payer/queries/useQueryPayerRows";
import { QUERY_PAYER_ROWS_ALL } from "../../../query/search/payer/queries/useQueryPayerRowsAll";
import { QUERY_PAYER_ROWS_SUMMARY } from "../../../query/search/payer/queries/useQueryPayerRowsSummary";

export type CreateClientInput = {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
};

/**
 * Maps the collected client fields onto the v3 profile. v1 stored one
 * `payerName` string; v3 wants the individual's first/last name separate, so
 * the modal now collects them as distinct fields and passes them through here
 * verbatim (no more lossy whitespace split).
 */
const buildProfile = (
  firstName?: string,
  lastName?: string,
  company?: string
): CounterpartyProfileWrite | undefined => {
  const profile: CounterpartyProfileWrite = {};

  const trimmedFirstName = firstName?.trim();
  const trimmedLastName = lastName?.trim();
  if (trimmedFirstName || trimmedLastName) {
    profile.individual = {
      firstName: trimmedFirstName || undefined,
      lastName: trimmedLastName || undefined
    };
  }

  const trimmedCompany = company?.trim();
  if (trimmedCompany) {
    profile.company = { doingBusinessAs: trimmedCompany };
  }

  return isEmpty(profile) ? undefined : profile;
};

/**
 * Creates a client (payer) on v3 and issues the Wingspan invite.
 *
 * v1 `createPayer` was one call that recorded the payer relationship (with the
 * payee-owned `payerName` / `payerCompanyName` / `payerEmailCC`). v3 splits it:
 * `createPayer` takes only the lean payer (email + profile), and the invite —
 * modeled in the invite-flows spec as a `PayerPayeeLinkRequest` — is now an
 * explicit `createPayerLinkRequest`. The payee-owned CC emails and QuickBooks
 * integration have no v3 create-time home and are intentionally dropped here
 * (see V3_MIGRATION_TRACKER.md); the richer v1 add-client modal keeps them for
 * the invoice / work-log flows.
 *
 * Reads stay on v1 (the Clients list/detail are still v1), so the mutation
 * explicitly invalidates the v1 payer caches; the v3 payer created this way
 * surfaces in the v1 `/search/payer-row` list once the backend indexes it.
 */
export const useCreateClient = (
  config?: WSMutationConfig<Payer, WSServiceError, CreateClientInput>
) =>
  useWSMutation<Payer, WSServiceError, CreateClientInput>(
    async input => {
      const payer = await payersApi.createPayer({
        payerCreate: {
          email: input.email,
          profile: buildProfile(input.firstName, input.lastName, input.company)
        }
      });

      await linkRequestsApi.createPayerLinkRequest({
        payerId: payer.id!,
        linkRequestNestedCreate: { targetEmail: input.email }
      });

      return payer;
    },
    {
      dependencies: [
        QUERY_PAYER_ROWS,
        QUERY_PAYER_ROWS_ALL,
        QUERY_PAYER_ROWS_SUMMARY,
        queriesPayers.listPayers.queryKeyBase
      ],
      ...config
    }
  );
